import { useMemo, useState } from "react";
import { clsx } from "clsx";
import {
  ArrowUpDown, Combine, Crop, Droplets, Eraser, FileOutput, FileType2, GitCompare,
  Hash, Highlighter, ImagePlus, Images, Info, Layers, LayoutGrid, ListPlus, Lock,
  LockOpen, Minimize2, RotateCw, ScanText, Search, ShieldOff, Split, SquareSlash,
  Stamp, TableRowsSplit, Type, Wrench, X,
} from "lucide-react";
import type { PdfTool, PdfToolCatalog } from "../types";

/** Ikony kafelków przychodzą z backendu po nazwie — mapa trzyma je w jednym miejscu. */
const ICONS: Record<string, typeof Combine> = {
  Combine, Split, FileOutput, Eraser, RotateCw, ArrowUpDown, Crop, LayoutGrid, Hash,
  Droplets, Stamp, Highlighter, SquareSlash, ScanText, Minimize2, Layers, ImagePlus,
  Images, FileType2, ShieldOff, Info, Lock, LockOpen, Type, GitCompare, ListPlus,
  TableRowsSplit,
};

const FAVOURITES_KEY = "elg5.pdf.tools.recent";

/** Ile razy dane narzędzie zostało użyte — steruje sekcją „Często używane”. */
export function readUsage(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(FAVOURITES_KEY) || "{}") as Record<string, number>;
  } catch {
    return {};
  }
}

export function bumpUsage(name: string): void {
  try {
    const usage = readUsage();
    usage[name] = (usage[name] || 0) + 1;
    localStorage.setItem(FAVOURITES_KEY, JSON.stringify(usage));
  } catch {
    /* tryb prywatny — częstotliwość to wygoda, nie dane */
  }
}

/** Czy narzędzie da się w ogóle uruchomić na tym, co jest zaznaczone. */
export function toolBlockedReason(
  tool: PdfTool,
  selected: { kind: string; office_kind?: string }[],
): string | null {
  if (selected.length === 0) return "Zaznacz plik na pulpicie.";
  if (tool.inputs === "one" && selected.length > 1) return "To narzędzie działa na jednym pliku.";
  if (tool.name === "merge" && selected.length < 2) return "Zaznacz co najmniej dwa pliki.";
  if (tool.name === "compare" && selected.length !== 2) return "Zaznacz dokładnie dwa pliki.";
  if (tool.accepts === "pdf" && selected.some((d) => d.kind !== "pdf"))
    return "To narzędzie działa tylko na PDF-ach.";
  if (tool.accepts === "image" && selected.some((d) => d.kind !== "image"))
    return "To narzędzie działa na plikach graficznych.";
  if (tool.accepts === "office" && selected.some((d) => d.kind !== "office"))
    return "To narzędzie działa na dokumentach biurowych.";
  // Narzędzia z grupy „Word / Excel” pracują na PLIKU, a nie na jego wydruku —
  // a otworzyć w ich własnym formacie umiemy tylko .docx, .xlsx i .odt. Stary .doc
  // czy .txt jest dla katalogu „dokumentem biurowym”, ale tu nic z nim nie zrobimy.
  if (tool.group === "office" && selected.some((d) => !d.office_kind))
    return "To narzędzie działa na plikach Worda (.docx), Excela (.xlsx) i ODT.";
  return null;
}

function Tile({
  tool,
  active,
  blocked,
  onPick,
}: {
  tool: PdfTool;
  active: boolean;
  blocked: string | null;
  onPick: () => void;
}) {
  const Icon = ICONS[tool.icon] ?? Wrench;
  return (
    <button
      type="button"
      onClick={onPick}
      title={blocked ? `${tool.hint} — ${blocked}` : tool.hint}
      aria-pressed={active}
      className={clsx(
        "group relative flex w-full flex-col items-center gap-1.5 border px-2 py-3 text-center transition-colors duration-100",
        active
          ? "border-[var(--color-primary)] bg-[var(--color-primary-dim)]"
          : "border-[var(--color-border)] bg-[var(--color-bg-surface)] hover:border-[var(--color-primary)] hover:bg-[var(--color-bg-elevated)]",
        blocked && "opacity-45",
      )}
    >
      <Icon
        size={20}
        className={clsx(
          "shrink-0",
          active ? "text-[var(--color-primary)]" : "text-[var(--color-text-secondary)]",
          !blocked && "group-hover:text-[var(--color-primary)]",
        )}
      />
      <span className="text-[11px] leading-tight text-[var(--color-text-primary)]">
        {tool.label}
      </span>
      {tool.heavy && (
        <span
          className="absolute right-1 top-1 h-1.5 w-1.5 bg-[var(--color-text-muted)]"
          title="Cięższa operacja — przy dużym pliku policzy się w tle"
        />
      )}
    </button>
  );
}

/**
 * Siatka kafelków — sercem modułu jest to, że KAŻDY kafelek to ta sama operacja,
 * którą wywołuje asystent. Lista przychodzi z backendu (rejestr operacji), więc
 * nie da się jej rozjechać z tym, co system naprawdę potrafi.
 */
export function ToolPalette({
  catalog,
  selectedDocs,
  activeTool,
  onPick,
}: {
  catalog: PdfToolCatalog | undefined;
  selectedDocs: { kind: string; office_kind?: string }[];
  activeTool: string | null;
  onPick: (tool: PdfTool) => void;
}) {
  const [query, setQuery] = useState("");
  const usage = useMemo(readUsage, [activeTool]);

  const tools = catalog?.tools ?? [];
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tools;
    return tools.filter(
      (t) =>
        t.label.toLowerCase().includes(q) ||
        t.hint.toLowerCase().includes(q) ||
        t.name.includes(q),
    );
  }, [tools, query]);

  const frequent = useMemo(
    () =>
      [...filtered]
        .filter((t) => (usage[t.name] || 0) > 0)
        .sort((a, b) => (usage[b.name] || 0) - (usage[a.name] || 0))
        .slice(0, 6),
    [filtered, usage],
  );

  const sections = (catalog?.groups ?? [])
    .map((g) => ({ ...g, items: filtered.filter((t) => t.group === g.key) }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="relative border-b border-[var(--color-border)] p-2">
        <Search
          size={14}
          className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]"
        />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Szukaj narzędzia…"
          aria-label="Szukaj narzędzia"
          className="input-base py-1.5 pl-7 pr-7 text-xs"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            aria-label="Wyczyść"
            className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
          >
            <X size={13} />
          </button>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {frequent.length > 0 && !query && (
          <section className="mb-3">
            <h3 className="section-label mb-1.5 px-0.5">Często używane</h3>
            <div className="grid grid-cols-3 gap-1.5">
              {frequent.map((t) => (
                <Tile
                  key={`fav-${t.name}`}
                  tool={t}
                  active={activeTool === t.name}
                  blocked={toolBlockedReason(t, selectedDocs)}
                  onPick={() => onPick(t)}
                />
              ))}
            </div>
          </section>
        )}

        {sections.map((group) => (
          <section key={group.key} className="mb-3">
            <h3 className="section-label mb-1.5 px-0.5">{group.label}</h3>
            <div className="grid grid-cols-3 gap-1.5">
              {group.items.map((t) => (
                <Tile
                  key={t.name}
                  tool={t}
                  active={activeTool === t.name}
                  blocked={toolBlockedReason(t, selectedDocs)}
                  onPick={() => onPick(t)}
                />
              ))}
            </div>
          </section>
        ))}

        {sections.length === 0 && (
          <p className="px-1 py-6 text-center text-xs text-[var(--color-text-muted)]">
            Nic nie pasuje do „{query}”.
          </p>
        )}
      </div>
    </div>
  );
}
